/**
 * Von der Richtung auf dem Bildschirm zur Richtung im Spiel.
 *
 * STEUERUNG NACH DEM BILDSCHIRM (Produktentscheidung vom 18.10.2026).
 *
 * "Nach oben" auf Tastatur, Joystick und Zielstick heisst: nach oben im
 * Bild - nicht "die Lane entlang". Die Kamera zeigt die Lane um 35 Grad
 * schraeg; bis heute lief der Held bei W die Lane entlang und damit schraeg
 * durchs Bild. Mit der animierten Figur wurde das sichtbar (Robin: "Wenn
 * ich W druecke, laufe ich nicht gerade nach oben, sondern schraeg").
 * Gewaehlt aus drei Moeglichkeiten - nach dem Bildschirm, nach der Lane
 * (wie bisher) oder die Kamera gerade stellen. Grund: Die Figur soll dorthin
 * laufen, wohin Taste oder Daumen zeigen; die schraege Kamera bleibt.
 *
 * Die Schraege der Kamera steht deshalb HIER und nur hier: Kamera und
 * Steuerung lesen denselben Wert und koennen nicht auseinanderlaufen.
 */

import type { PlayerSide } from '@dueline/shared';

/**
 * Wie schraeg die Lane im Bild liegt, in Grad (siehe CAMERA in GameView).
 *
 * Das Vorzeichen entscheidet, nach welcher Seite sie kippt - mit -35 liegt
 * sie von unten links nach oben rechts.
 */
export const LANE_SCHRAEGE: number = -35;

/**
 * Die waagerechte Blickrichtung der Kamera, Laenge 1, in Spielkoordinaten.
 *
 * Fuer Blau liegt der Gegner Richtung groesserer x, fuer Orange Richtung
 * kleinerer - die Kamera der Gegenseite steht um 180 Grad gedreht, damit
 * beide ihre eigene Seite unten links sehen.
 */
export function kameraBlick(seite: PlayerSide): { x: number; y: number } {
  const grad = seite === 'blue' ? LANE_SCHRAEGE : 180 + LANE_SCHRAEGE;
  const bogen = (grad * Math.PI) / 180;
  return { x: Math.cos(bogen), y: Math.sin(bogen) };
}

/**
 * Eine Richtung auf dem Bildschirm als Richtung im Spiel.
 *
 * "Rechts im Bild" ist die Senkrechte zum Blick: (-y, x). Die Laenge bleibt
 * erhalten - eine Drehung darf nicht schneller machen.
 *
 * @param rechts Anteil nach rechts im Bild (negativ: links)
 * @param oben   Anteil nach oben im Bild (negativ: unten)
 */
export function bildschirmZuSpiel(
  rechts: number,
  oben: number,
  seite: PlayerSide,
): { x: number; y: number } {
  const blick = kameraBlick(seite);
  return {
    x: oben * blick.x - rechts * blick.y,
    y: oben * blick.y + rechts * blick.x,
  };
}

/**
 * Eine Richtung im Spiel als Richtung auf dem Bildschirm - die Umkehrung
 * von bildschirmZuSpiel (Produktentscheidung vom 25.10.2026).
 *
 * Gebraucht wird sie seit der Maussteuerung: Ein angeklickter Punkt liegt
 * im SPIEL, die Bewegung wird aber als Bildschirmrichtung verschickt und
 * unterwegs gedreht. Ohne diese Umkehrung wird die Klickrichtung ein
 * zweites Mal gedreht - der Hero lief in einem Bogen zum Ziel statt
 * geradeaus (im Spiel aufgefallen, Robin am 25.10.2026).
 *
 * Dieselbe Drehung rueckwaerts: Die Lange bleibt erhalten.
 */
export function spielZuBildschirm(
  x: number,
  y: number,
  seite: PlayerSide,
): { rechts: number; oben: number } {
  const blick = kameraBlick(seite);
  return {
    rechts: -x * blick.y + y * blick.x,
    oben: x * blick.x + y * blick.y,
  };
}
